import { Button, Dialog, DialogContent, DialogTitle, Divider, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import type {
  ObjectSelectConfiguration,
  WorkflowActivity,
  WorkflowActivityChangeSet,
  WorkflowActivityEditConfiguration,
} from "../../../../../../api/types/workflowTypes";
import ActivityChangeSetCreationDialog from "./ActivityChangeSetCreationDialog";
import {
  getInitialChangeSetEditState,
  groupFieldsBySections,
  mapStateToUpdateWorkflowActivityChangeSetsRequest,
  updateChangeSetFieldAction,
  updateChangeSetFieldsAction,
} from "./changeSetEditState";
import ChangeSetFieldEditRow from "./ChangeSetFieldEditRow";
import { buildObjectFieldUpdates, loadObjectSelectOptions } from "./objectSelectOptionsHelper";
import RelatedChangeSetsEditTable from "./RelatedChangeSetsEditTable";

interface Props {
  workflowId: string;
  activityId: string;
  activityChangeSet: WorkflowActivityChangeSet;
  editConfiguration?: WorkflowActivityEditConfiguration;
  onClose: () => void;
  onSaved: (updatedActivity: WorkflowActivity) => void;
}

const updateWorkflowActivityChangeSets = withErrorHandling(adminApi.updateWorkflowActivityChangeSets);

const ChangeSetEditDialog = ({
  workflowId,
  activityId,
  activityChangeSet,
  editConfiguration,
  onClose,
  onSaved,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [editState, setEditState] = useState(() => getInitialChangeSetEditState(activityChangeSet, editConfiguration));
  const [saving, setSaving] = useState(false);
  const [selectExistingDialogOpen, setSelectExistingDialogOpen] = useState(false);

  const objectSelectConfigurations = useMemo(
    () => editConfiguration?.objectSelectConfigurations ?? [],
    [editConfiguration?.objectSelectConfigurations]
  );

  const loadOptions = useCallback(
    () => loadObjectSelectOptions(objectSelectConfigurations, adminApi.searchObjects),
    [objectSelectConfigurations]
  );

  const [objectSelectOptionsResp, fetchError, { isFetching }] = useFetch(loadOptions, {
    disabled: objectSelectConfigurations.length === 0,
  });

  const handleSave = async () => {
    setSaving(true);
    const payload = mapStateToUpdateWorkflowActivityChangeSetsRequest(editState, activityChangeSet);
    const [updatedChangeSet, error] = await updateWorkflowActivityChangeSets(workflowId, activityId, payload);
    setSaving(false);

    if (error) {
      logError(error, "[ChangeSetEditDialog] updateWorkflowActivityChangeSets");
      sendNotificationError(error.message);
      return;
    }

    onSaved(updatedChangeSet);
  };

  const handleFieldChange = (fieldName: string, value: unknown) => {
    setEditState(updateChangeSetFieldAction(fieldName, value));
  };

  const handleObjectChange = (configuration: ObjectSelectConfiguration, selectedObject: ObjectFieldValues | null) => {
    setEditState(updateChangeSetFieldsAction(buildObjectFieldUpdates(configuration, selectedObject)));
  };

  if (fetchError) {
    logError(fetchError, "[ChangeSetEditDialog] loadObjectSelectOptions");
  }

  const objectSelectOptions = objectSelectOptionsResp ?? {};

  const fieldGroups = groupFieldsBySections(editState.fields, activityChangeSet.sections);

  return (
    <>
      <Dialog open={!selectExistingDialogOpen} maxWidth="xl" fullWidth>
        <DialogTitle>Edit Activity</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />

        <DialogContent sx={{ height: "80vh" }}>
          {fetchError && <DataLoadingFailed title="Failed to fetch required entities" />}

          {!fetchError && isFetching && <InlineLoader />}

          {!fetchError && !isFetching && (
            <Stack spacing={3}>
              {fieldGroups.map((group, groupIndex) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Paper key={`${group.name ?? "ungrouped"}-${groupIndex}`} variant="outlined" sx={{ p: 2 }}>
                  {group.name !== undefined && (
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      {group.name}
                    </Typography>
                  )}
                  {group.fields.map((field) => (
                    <ChangeSetFieldEditRow
                      key={field.name}
                      field={field}
                      editConfiguration={editConfiguration}
                      objectSelectOptions={objectSelectOptions}
                      onFieldChange={handleFieldChange}
                      onObjectChange={handleObjectChange}
                    />
                  ))}
                </Paper>
              ))}

              {editState.relatedChangeSets.map((relatedChangeSet) => (
                <Paper key={relatedChangeSet.name} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {relatedChangeSet.name}
                  </Typography>
                  <RelatedChangeSetsEditTable group={relatedChangeSet} setEditState={setEditState} />
                </Paper>
              ))}

              {Boolean(editConfiguration?.objectTypeOptions?.length) && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setSelectExistingDialogOpen(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Select existing entity instead
                </Button>
              )}
            </Stack>
          )}
        </DialogContent>

        <Divider />
        <DialogButtons onCancel={onClose}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            loading={saving}
            disabled={!editState.isDirty || !editState.isValid}
          >
            Save
          </Button>
        </DialogButtons>
      </Dialog>
      {selectExistingDialogOpen && editConfiguration?.objectTypeOptions !== undefined && (
        <ActivityChangeSetCreationDialog
          workflowId={workflowId}
          activityId={activityId}
          objectTypeOptions={editConfiguration.objectTypeOptions}
          onClose={() => setSelectExistingDialogOpen(false)}
          onSaved={onSaved}
        />
      )}
    </>
  );
};

export default ChangeSetEditDialog;
